import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowLeft, ChevronLeft, ChevronRight, EyeOff, Loader2, Swords } from 'lucide-react'
import { useTeamStore } from '../store/useTeamStore'
import useAppStore from '../../../store/useAppStore'
import TeamGameCard from '../components/TeamGameCard'
import { accentColor } from '../utils/teamColors'
import { MODE_KINDS, modeIcon, modeLabel, roundPath, statsErrorText } from '../utils/teamStats'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import { hapticImpact } from '../../../utils/haptic'
import useAudioManager from '../../../hooks/useAudioManager'

/**
 * История командных игр банды (`/teams/:slug/games`, docs/team-stats-api.md §4).
 *
 * 20 игр на страницу, свежие сверху, необязательный фильтр по режиму.
 * По нажатию раунд открывается на своей уже существующей странице (`ref`),
 * а сессия кейсов/апгрейдера/контракта — на экране детализации §5: родной
 * страницы у неё нет.
 */
const TeamGames = () => {
  const { slug } = useParams()
  const isGuest = useAppStore((state) => state.isGuest)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { playSound } = useAudioManager({ pick: 'pick', select: 'select' })
  const { team, loadTeam, games, isGamesLoading, gamesError, loadGames, resetGames } = useTeamStore()

  const [kind, setKind] = useState(null)
  const [page, setPage] = useState(1)

  // `/teams/my/games` — свою банду сначала надо опознать: ручка работает по slug
  const gamesSlug = slug === 'my' ? team?.id : slug

  useEffect(() => {
    loadTeam(slug)
    return () => resetGames()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isGuest])

  useEffect(() => {
    if (gamesSlug) loadGames(gamesSlug, { page, kind: kind || undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesSlug, page, kind, isGuest])

  const accent = accentColor(team?.colors)

  const selectKind = (value) => {
    if (value === kind) return
    playSound('pick')
    hapticImpact('light')
    setPage(1)
    setKind(value)
  }

  /**
   * Раунд ведём на его родную страницу (`ref`), сессию — на экран детализации.
   * Фолбэк на детализацию нужен и раунду: если бэкенд когда-нибудь добавит
   * режим, страницы которого у нас нет, `roundPath` вернёт null.
   */
  const openGame = (game) => {
    playSound('select')
    const path = game.is_session ? null : roundPath(game.ref)
    navigate(path || `/teams/${gamesSlug}/games/${game.id}`)
  }

  const chips = [null, ...MODE_KINDS]

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-3xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button
            onClick={() => {
              playSound('select')
              if (window.history.length > 1) navigate(-1)
              else navigate(`/teams/${slug}`)
            }}
            className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-2xl text-white">{t('team.stats.gamesTitle', 'Командные игры')}</h1>
            {team?.name && <p className="truncate text-xs text-text-secondary">{team.name}</p>}
          </div>
        </motion.div>

        {/* Фильтр по режиму: чипы с переносом на следующие строки */}
        <motion.div
          variants={SECTION_VARIANT}
          className="flex flex-wrap gap-2"
        >
          {chips.map((value) => {
            const isActive = value === kind
            const Icon = value ? modeIcon(value) : Swords
            return (
              <button
                key={value || 'all'}
                onClick={() => selectKind(value)}
                className="flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-all active:scale-[0.98]"
                style={isActive
                  ? { borderColor: `${accent}55`, backgroundColor: `${accent}14`, color: accent }
                  : { borderColor: 'rgba(255,255,255,0.10)', backgroundColor: 'rgba(255,255,255,0.03)', color: '#A0A0A0' }}
              >
                <Icon size={13} />
                {value ? modeLabel(value, t) : t('team.stats.allModes', 'Все режимы')}
              </button>
            )
          })}
        </motion.div>

        {gamesError ? (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} flex flex-col items-center p-8 text-center`}>
            <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
              <EyeOff size={24} className="text-text-secondary" />
            </span>
            <p className="text-sm text-text-secondary">{statsErrorText(gamesError, t)}</p>
          </motion.div>
        ) : isGamesLoading && games.games.length === 0 ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-accent-primary" />
          </div>
        ) : games.games.length === 0 ? (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} flex flex-col items-center p-8 text-center`}>
            <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
              <Swords size={24} className="text-text-secondary" />
            </span>
            <p className="text-sm text-text-secondary">
              {t('team.stats.gamesEmpty', 'Командных игр пока нет: нужно, чтобы двое из банды сыграли вместе')}
            </p>
          </motion.div>
        ) : (
          <motion.div variants={SECTION_VARIANT} className="space-y-2">
            {games.games.map((game) => (
              <TeamGameCard key={game.id} game={game} accent={accent} onOpen={openGame} />
            ))}
          </motion.div>
        )}

        {games.pages > 1 && (
          <motion.div variants={SECTION_VARIANT} className="flex items-center justify-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isGamesLoading}
              className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
              aria-label={t('common.back', 'Назад')}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm tabular-nums text-text-secondary">{page} / {games.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(games.pages, p + 1))}
              disabled={page >= games.pages || isGamesLoading}
              className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
              aria-label={t('team.nextPage', 'Дальше')}
            >
              <ChevronRight size={18} />
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  )
}

export default TeamGames
